import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Users, FileText, Mail, Smartphone, Cloud, ArrowRight, HardHat, ShieldCheck, Database, History, Calendar, TrendingUp, Layers, Infinity as InfinityIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import FeedbackButtons from "@/components/FeedbackButtons";

const features = [
  { icon: InfinityIcon, text: "Unlimited Quotes" },
  { icon: Users, text: "Customer Management" },
  { icon: Database, text: "Customer Database" },
  { icon: History, text: "Quote History" },
  { icon: Mail, text: "Email Quote Delivery" },
  { icon: Calendar, text: "Job Scheduling Calendar" },
  { icon: TrendingUp, text: "Revenue Tracking" },
  { icon: Layers, text: "Unlimited Projects" },
  { icon: Smartphone, text: "Mobile Friendly" },
  { icon: Cloud, text: "Cloud Storage" },
];

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (user && user.subscription_status === "active") nav("/dashboard", { replace: true });
  }, [user, nav]);

  return (
    <div className="min-h-screen bg-white relative overflow-hidden" data-testid="landing-page">
      {/* Subtle gray geometric overlays */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-slate-50" />
        <div className="absolute top-[40%] -left-40 w-[500px] h-[500px] rounded-full bg-slate-100/70" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
      </div>

      {/* Nav */}
      <nav className="relative max-w-6xl mx-auto px-6 py-5 flex items-center justify-between" data-testid="landing-nav">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/20">
            <HardHat className="w-5 h-5 text-white" />
          </div>
          <div className="font-display font-extrabold uppercase tracking-widest text-slate-900">Quick Quotes</div>
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/login" className="text-sm font-medium text-slate-700 hover:text-slate-900 px-3 py-2" data-testid="landing-login">Sign in</Link>
          <Button data-testid="landing-cta-nav" onClick={() => nav("/signup")} className="bg-blue-600 hover:bg-blue-700 text-white">
            Start for $3.99/mo
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative max-w-5xl mx-auto px-6 pt-16 pb-24 text-center" data-testid="landing-hero">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-200 bg-white/80 backdrop-blur-sm text-xs font-semibold text-slate-700">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          Quick quotes, live in 60 seconds.
        </div>
        <h1 className="mt-6 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05]">
          Create Professional<br />Quotes in Seconds
        </h1>
        <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto">
          Save time, win more jobs, and manage all your customer quotes in one place.
        </p>
        <div className="mt-6 flex justify-center">
          <div
            data-testid="trust-badge"
            className="inline-flex items-center gap-2 pl-2 pr-4 py-1.5 rounded-full bg-blue-600/5 border border-blue-600/20 text-blue-700 shadow-sm shadow-blue-600/5"
          >
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white">
              <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2.5} />
            </span>
            <span className="text-sm font-bold tracking-wide">
              Trusted by 1,000s of businesses
            </span>
          </div>
        </div>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Button data-testid="landing-cta-hero" onClick={() => nav("/signup")} className="bg-blue-600 hover:bg-blue-700 text-white h-12 px-8 text-base font-bold">
            Start for $3.99/Month <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <Button variant="outline" onClick={() => nav("/login")} className="h-12 px-6">I already have an account</Button>
        </div>
      </section>

      {/* Pricing */}
      <section className="relative max-w-5xl mx-auto px-6 py-20" data-testid="pricing-section">
        <div className="text-center">
          <div className="text-xs font-bold uppercase tracking-widest text-blue-600">Pricing</div>
          <h2 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl text-slate-900">Start free. Upgrade when you're busy.</h2>
          <p className="mt-3 text-slate-600">No contracts. Cancel anytime.</p>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl mx-auto">
          {/* Free */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-900/5 overflow-hidden flex flex-col" data-testid="plan-free">
            <div className="bg-white px-6 py-5 border-b border-slate-100">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Free Plan</div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="font-display font-extrabold text-5xl text-slate-900">$0</span>
                <span className="text-slate-400 font-medium">/forever</span>
              </div>
              <div className="mt-1 text-xs text-slate-500">Perfect for trying it out.</div>
            </div>
            <ul className="p-6 space-y-3 flex-1">
              <li className="flex items-center gap-3 text-sm text-slate-700">
                <div className="w-6 h-6 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center"><Check className="w-3.5 h-3.5 text-slate-600" strokeWidth={3} /></div>
                <b>3 quotes total</b>
              </li>
              <li className="flex items-center gap-3 text-sm text-slate-700">
                <div className="w-6 h-6 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center"><Check className="w-3.5 h-3.5 text-slate-600" strokeWidth={3} /></div>
                PDF quote exports
              </li>
              <li className="flex items-center gap-3 text-sm text-slate-700">
                <div className="w-6 h-6 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center"><Check className="w-3.5 h-3.5 text-slate-600" strokeWidth={3} /></div>
                Mobile friendly
              </li>
            </ul>
            <div className="p-6 pt-0">
              <Button data-testid="plan-free-cta" variant="outline" onClick={() => nav("/signup?plan=free")} className="w-full h-12 font-bold tracking-wider uppercase text-sm">
                Start Free
              </Button>
            </div>
          </div>

          {/* Pro */}
          <div className="rounded-2xl border-2 border-blue-600 bg-white shadow-xl shadow-blue-600/10 overflow-hidden flex flex-col relative" data-testid="plan-pro">
            <div className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-widest bg-blue-600 text-white px-2 py-1 rounded">Most popular</div>
            <div className="bg-slate-900 text-white px-6 py-5">
              <div className="text-xs font-bold uppercase tracking-widest text-blue-300">Pro Plan</div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="font-display font-extrabold text-5xl">$3.99</span>
                <span className="text-slate-400 font-medium">/month</span>
              </div>
              <div className="mt-1 text-xs text-slate-400">Auto-renews every 30 days.</div>
            </div>
            <ul className="p-6 space-y-3 flex-1">
              {features.map((f) => (
                <li key={f.text} className="flex items-center gap-3 text-sm text-slate-700">
                  <div className="w-6 h-6 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center"><Check className="w-3.5 h-3.5 text-blue-600" strokeWidth={3} /></div>
                  {f.text}
                </li>
              ))}
            </ul>
            <div className="p-6 pt-0">
              <Button data-testid="pricing-cta" onClick={() => nav("/signup")} className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold tracking-wider uppercase text-sm">
                Start for $3.99/Month
              </Button>
              <p className="mt-3 text-[11px] text-center text-slate-500">Secure checkout by Stripe · Cancel anytime</p>
            </div>
          </div>
        </div>
      </section>

      {/* Promo code CTA (contractors with a referral code bypass checkout) */}
      <section className="relative max-w-6xl mx-auto px-6 pb-14" data-testid="promo-section">
        <div className="rounded-2xl bg-slate-900 text-white px-6 sm:px-10 py-8 flex flex-col sm:flex-row items-center gap-6 shadow-xl shadow-slate-900/20">
          <div className="w-14 h-14 shrink-0 rounded-full bg-blue-600 flex items-center justify-center">
            <HardHat className="w-7 h-7 text-white" strokeWidth={2.4} />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <div className="text-xs font-bold uppercase tracking-widest text-blue-300">Got a promo code?</div>
            <div className="mt-1 font-display text-2xl sm:text-3xl font-extrabold">
              Create a free Pro account in 30 seconds.
            </div>
            <p className="mt-1 text-sm text-slate-300">
              Have a code from a partner, friend, or trade show? Enter it on signup — no card required.
            </p>
          </div>
          <Button
            data-testid="landing-promo-cta"
            onClick={() => nav("/signup?promo=1")}
            className="h-12 px-6 bg-blue-600 hover:bg-blue-700 text-white font-bold tracking-wider uppercase text-sm whitespace-nowrap"
          >
            Redeem Code <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      <footer className="relative border-t border-slate-200 py-10 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} Quick Quotes · <Link to="/login" className="hover:text-slate-900">Sign in</Link> · <Link to="/signup" className="hover:text-slate-900">Create account</Link> · <Link to="/signup?promo=1" className="hover:text-slate-900" data-testid="landing-promo-footer">Redeem promo code</Link> · <FeedbackButtons variant="link" allowAnonymous />
      </footer>
    </div>
  );
}
